
import React, { useState, useMemo } from 'react';
import { RabEntry } from '../../types';
import { DEFAULT_CATEGORY_ORDER, categoryKey, groupEntriesByCategory } from './ufsaOrdering';
import { Trash2, Edit2, Check, X, Pencil, Scale, ChevronDown, ChevronRight, Filter, Layers, LayoutGrid, Trees, Zap, Home, Footprints, Box, AlertTriangle, ShieldCheck, ArrowUp, ArrowDown, FolderInput } from 'lucide-react';

// Kategorie-Optionen tragen ein Präfix, damit eine Kategorie, die zufällig
// wie die Sonderoption heißt, nicht mit ihr kollidiert.
const CATEGORY_OPTION_PREFIX = 'cat:';
const NEW_CATEGORY_OPTION = 'new';

interface EntryListProps {
  entries: RabEntry[];
  onDelete: (id: string) => void;
  onEdit: (id: string, updatedEntry: Omit<RabEntry, 'id'>) => void;
  onMove?: (id: string, direction: 'up' | 'down') => void;
  onMoveToCategory?: (id: string, category: string) => void;
  onCategoryRename: (oldName: string, newName: string) => void;
  categoryOrder?: string[];
}

const getCategoryIcon = (category: string) => {
  const norm = category.toLowerCase();
  if (norm.includes('dach')) return <Layers className="w-4 h-4" />;
  if (norm.includes('fassade')) return <LayoutGrid className="w-4 h-4" />;
  if (norm.includes('außen') || norm.includes('grün')) return <Trees className="w-4 h-4" />;
  if (norm.includes('technik') || norm.includes('elektro')) return <Zap className="w-4 h-4" />;
  if (norm.includes('innen')) return <Home className="w-4 h-4" />;
  if (norm.includes('verkehr') || norm.includes('weg')) return <Footprints className="w-4 h-4" />;
  return <Box className="w-4 h-4" />;
};

export const EntryList: React.FC<EntryListProps> = ({ entries, onDelete, onEdit, onMove, onMoveToCategory, onCategoryRename, categoryOrder }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const [editForm, setEditForm] = useState<Omit<RabEntry, 'id'>>({
    category: '', component: '', activity: '', hazards: '', measures: '', frequency: '', regulations: ''
  });

  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Dieselbe Gruppierung wie im PDF (ufsaOrdering) — die Liste zeigt, was
  // gedruckt wird, in genau dieser Reihenfolge.
  const groupedEntries = useMemo(
    () => groupEntriesByCategory(entries, categoryOrder),
    [entries, categoryOrder],
  );

  const categories = groupedEntries.map(g => g.category);

  // Ziele für „In Kategorie verschieben": eingestellte Reihenfolge plus die
  // Kategorien der Einträge, je Schlüssel einmal; vorhandene Gruppen mit
  // ihrer Schreibweise aus der Liste.
  const moveTargets = useMemo(() => {
    const shownName = new Map(groupedEntries.map(g => [categoryKey(g.category), g.category]));
    const seen = new Set<string>();
    const targets: { key: string; label: string }[] = [];
    for (const name of [...(categoryOrder?.length ? categoryOrder : DEFAULT_CATEGORY_ORDER), ...shownName.values()]) {
      const key = categoryKey(name);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push({ key, label: shownName.get(key) ?? name.trim() });
    }
    return targets;
  }, [groupedEntries, categoryOrder]);

  // Eintrag, für den gerade eine neue Kategorie eingetippt wird.
  const [newCategoryFor, setNewCategoryFor] = useState<string | null>(null);
  const [newCategoryDraft, setNewCategoryDraft] = useState('');

  const moveToCategory = (entry: RabEntry, target: string | undefined) => {
    if (!onMoveToCategory) return;
    target = target?.trim();
    if (!target || categoryKey(target) === categoryKey(entry.category)) return;
    onMoveToCategory(entry.id, target);
    // Zielgruppe aufklappen, damit der Eintrag dort sichtbar ist.
    const targetKey = categoryKey(target);
    setCollapsedCategories(prev => {
      const next = new Set([...prev].filter(c => categoryKey(c) !== targetKey));
      return next.size === prev.size ? prev : next;
    });
  };

  const startEditing = (entry: RabEntry) => {
    setEditingId(entry.id);
    setEditForm({ ...entry });
  };

  const handleSave = () => {
    if (editingId) {
      onEdit(editingId, editForm);
      setEditingId(null);
    }
  };

  const toggleCategoryCollapse = (category: string) => {
    const newSet = new Set(collapsedCategories);
    if (newSet.has(category)) newSet.delete(category);
    else newSet.add(category);
    setCollapsedCategories(newSet);
  };

  const saveCategoryRename = () => {
    if (editingCategory && newCategoryName.trim()) {
      onCategoryRename(editingCategory, newCategoryName);
    }
    setEditingCategory(null);
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-slate-200 border-dashed flex flex-col items-center">
        <div className="bg-slate-50 p-4 rounded-full mb-3">
            <Box className="w-8 h-8 text-slate-300" />
        </div>
        <h3 className="text-base font-bold text-slate-700">Keine Einträge</h3>
        <p className="text-sm text-slate-500 max-w-xs mx-auto">Nutzen Sie das Formular oben, um den Katalog zu füllen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <datalist id="category-suggestions-edit">
        <option value="Dach" /><option value="Fassade" /><option value="Außenanlage" />
        <option value="Technik" /><option value="Innenraum" /><option value="Verkehrswege" /><option value="Sonstiges" />
      </datalist>

      <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
         <div className="flex items-center gap-1.5 pr-2 border-r border-slate-200 mr-2 text-slate-400">
            <Filter className="w-4 h-4" />
            <span className="text-xs font-bold uppercase">Filter</span>
         </div>
         
         <button
            onClick={() => setActiveFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
               activeFilter === 'all' 
               ? 'bg-slate-800 text-white shadow-md' 
               : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
         >
            Alle ({entries.length})
         </button>

         {categories.map(cat => (
            <button
               key={cat}
               onClick={() => setActiveFilter(cat)}
               className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  activeFilter === cat 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-blue-50 hover:text-blue-600'
               }`}
            >
               {getCategoryIcon(cat)}
               {cat}
            </button>
         ))}
      </div>

      <div className="space-y-6">
        {groupedEntries.map(({ category, entries: items }) => {
          if (activeFilter !== 'all' && activeFilter !== category) return null;

          const isCollapsed = collapsedCategories.has(category);

          return (
            <div key={category} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
              
              <div 
                 className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200 cursor-pointer group select-none"
                 onClick={() => toggleCategoryCollapse(category)}
              >
                 <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-md transition-colors ${isCollapsed ? 'bg-slate-200 text-slate-500' : 'bg-blue-100 text-blue-600'}`}>
                       {getCategoryIcon(category)}
                    </div>
                    
                    {editingCategory === category ? (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                           <input 
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              className="px-2 py-1 border border-blue-400 rounded text-sm font-bold text-slate-800 outline-none w-48"
                              autoFocus
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter') saveCategoryRename();
                                 if (e.key === 'Escape') setEditingCategory(null);
                              }}
                           />
                           <button onClick={saveCategoryRename} className="p-1 text-green-600 bg-green-50 rounded border border-green-200"><Check className="w-3 h-3" /></button>
                           <button onClick={() => setEditingCategory(null)} className="p-1 text-red-500 bg-red-50 rounded border border-red-200"><X className="w-3 h-3" /></button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 group/title">
                           <h3 className="font-bold text-slate-700 text-sm uppercase tracking-wide">{category}</h3>
                           <span className="text-xs text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-200 font-medium">
                              {items.length}
                           </span>
                           <button 
                              onClick={(e) => { e.stopPropagation(); setEditingCategory(category); setNewCategoryName(category); }}
                              className="opacity-0 group-hover/title:opacity-100 p-1 text-slate-400 hover:text-blue-600 transition-all"
                           >
                              <Pencil className="w-3 h-3" />
                           </button>
                        </div>
                    )}
                 </div>

                 <div className="text-slate-400 group-hover:text-slate-600 transition-colors">
                    {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5 rotate-90 transition-transform" />}
                 </div>
              </div>

              {!isCollapsed && (
                 <div className="divide-y divide-slate-100">
                    {items.map((entry, idxInGroup) => {
                       const isEditing = editingId === entry.id;
                       // Die Pfeile verschieben innerhalb der Kategorie; am
                       // Rand der Gruppe sind sie aus — vorher tauschten sie
                       // still mit dem Nachbarn einer ANDEREN Kategorie.
                       const canUp = idxInGroup > 0;
                       const canDown = idxInGroup < items.length - 1;

                       if (isEditing) {
                          return (
                             <div key={entry.id} className="p-5 bg-blue-50/30">
                                <div className="space-y-4">
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Bauteil</label>
                                         <input 
                                            value={editForm.component} 
                                            onChange={e => setEditForm({...editForm, component: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Tätigkeit</label>
                                         <input 
                                            value={editForm.activity} 
                                            onChange={e => setEditForm({...editForm, activity: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                   </div>
                                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Kategorie</label>
                                         <input 
                                            list="category-suggestions-edit"
                                            value={editForm.category} 
                                            onChange={e => setEditForm({...editForm, category: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Turnus</label>
                                         <input 
                                            value={editForm.frequency} 
                                            onChange={e => setEditForm({...editForm, frequency: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Regelwerke</label>
                                         <input 
                                            value={editForm.regulations} 
                                            onChange={e => setEditForm({...editForm, regulations: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                   </div>
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Gefährdung</label>
                                         <textarea 
                                            rows={3}
                                            value={editForm.hazards} 
                                            onChange={e => setEditForm({...editForm, hazards: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                      <div>
                                         <label className="text-[11px] font-bold text-slate-500 uppercase">Maßnahme</label>
                                         <textarea 
                                            rows={3}
                                            value={editForm.measures} 
                                            onChange={e => setEditForm({...editForm, measures: e.target.value})}
                                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                                         />
                                      </div>
                                   </div>
                                   <div className="flex justify-end gap-2">
                                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded">Abbrechen</button>
                                      <button onClick={handleSave} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded font-medium hover:bg-blue-700">Speichern</button>
                                   </div>
                                </div>
                             </div>
                          );
                       }

                       return (
                          <div key={entry.id} className="p-4 hover:bg-slate-50 group transition-colors">
                             <div className="flex flex-col gap-3">
                                
                                <div className="flex justify-between items-start gap-4">
                                   <div>
                                      <h4 className="text-base font-bold text-slate-800 leading-tight">{entry.component}</h4>
                                      <p className="text-sm font-medium text-blue-600 mt-0.5">{entry.activity}</p>
                                   </div>
                                   <div className="flex flex-col items-end gap-1.5">
                                      <div className="flex items-center gap-2">
                                         {onMoveToCategory && newCategoryFor === entry.id && (
                                            <div className="flex items-center gap-1">
                                               <input
                                                  value={newCategoryDraft}
                                                  onChange={e => setNewCategoryDraft(e.target.value)}
                                                  placeholder="Neue Kategorie"
                                                  className="px-2 py-1 border border-blue-400 rounded text-xs font-medium text-slate-800 outline-none w-36"
                                                  autoFocus
                                                  onKeyDown={e => {
                                                     if (e.key === 'Enter') { moveToCategory(entry, newCategoryDraft); setNewCategoryFor(null); }
                                                     if (e.key === 'Escape') setNewCategoryFor(null);
                                                  }}
                                               />
                                               <button onClick={() => { moveToCategory(entry, newCategoryDraft); setNewCategoryFor(null); }} className="p-1 text-green-600 bg-green-50 rounded border border-green-200" title="Verschieben"><Check className="w-3 h-3" /></button>
                                               <button onClick={() => setNewCategoryFor(null)} className="p-1 text-red-500 bg-red-50 rounded border border-red-200" title="Abbrechen"><X className="w-3 h-3" /></button>
                                            </div>
                                         )}
                                         {onMoveToCategory && newCategoryFor !== entry.id && (
                                            <label className="flex items-center gap-1 pl-1.5 rounded border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:text-blue-600 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100" title="In andere Kategorie verschieben">
                                               <FolderInput className="w-3.5 h-3.5 flex-shrink-0" />
                                               <select
                                                  value={CATEGORY_OPTION_PREFIX + categoryKey(entry.category)}
                                                  onChange={e => {
                                                     const value = e.target.value;
                                                     if (value === NEW_CATEGORY_OPTION) {
                                                        setNewCategoryDraft('');
                                                        setNewCategoryFor(entry.id);
                                                     } else {
                                                        moveToCategory(entry, moveTargets.find(t => CATEGORY_OPTION_PREFIX + t.key === value)?.label);
                                                     }
                                                  }}
                                                  aria-label="In andere Kategorie verschieben"
                                                  className="max-w-[9rem] truncate bg-transparent py-1 pr-1 text-xs font-medium text-slate-600 outline-none cursor-pointer"
                                               >
                                                  {moveTargets.map(t => (
                                                     <option key={t.key} value={CATEGORY_OPTION_PREFIX + t.key}>{t.label}</option>
                                                  ))}
                                                  <option value={NEW_CATEGORY_OPTION}>Neue Kategorie …</option>
                                               </select>
                                            </label>
                                         )}
                                         {onMove && (
                                            <>
                                               <button onClick={() => onMove(entry.id, 'up')} disabled={!canUp} className={`p-1.5 rounded transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 ${canUp ? 'text-slate-400 hover:text-slate-800 hover:bg-slate-200' : 'text-slate-200 cursor-not-allowed'}`} title={canUp ? 'Innerhalb der Kategorie nach oben verschieben' : 'Bereits der erste Eintrag der Kategorie'}>
                                                  <ArrowUp className="w-4 h-4" />
                                               </button>
                                               <button onClick={() => onMove(entry.id, 'down')} disabled={!canDown} className={`p-1.5 rounded transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 ${canDown ? 'text-slate-400 hover:text-slate-800 hover:bg-slate-200' : 'text-slate-200 cursor-not-allowed'}`} title={canDown ? 'Innerhalb der Kategorie nach unten verschieben' : 'Bereits der letzte Eintrag der Kategorie'}>
                                                  <ArrowDown className="w-4 h-4" />
                                               </button>
                                            </>
                                         )}
                                         <button onClick={() => startEditing(entry)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-100 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-all">
                                            <Edit2 className="w-4 h-4" />
                                         </button>
                                         <button onClick={() => onDelete(entry.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-100 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-all">
                                            <Trash2 className="w-4 h-4" />
                                         </button>
                                      </div>
                                      
                                      <div className="flex gap-2 flex-wrap justify-end">
                                        {entry.frequency && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200">
                                                {entry.frequency}
                                            </span>
                                        )}
                                        {entry.regulations && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200 gap-1" title={entry.regulations}>
                                                <Scale className="w-3 h-3" /> Normen
                                            </span>
                                        )}
                                      </div>
                                   </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                                   
                                   <div className="flex gap-2 items-start bg-red-50/50 p-2.5 rounded-lg border border-red-50/50">
                                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                      <div>
                                         <span className="text-[11px] font-bold text-red-400 uppercase block mb-0.5">Gefährdung</span>
                                         <p className="text-sm text-slate-700 leading-snug">{entry.hazards}</p>
                                      </div>
                                   </div>

                                   <div className="flex gap-2 items-start bg-green-50/50 p-2.5 rounded-lg border border-green-50/50">
                                      <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                      <div>
                                         <span className="text-[11px] font-bold text-green-500 uppercase block mb-0.5">Maßnahme</span>
                                         <p className="text-sm text-slate-700 leading-snug whitespace-pre-wrap">{entry.measures}</p>
                                      </div>
                                   </div>

                                </div>

                             </div>
                          </div>
                       );
                    })}
                 </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
